"use client";

import React from "react";
import { cn } from "@/lib/utils";

type FormFieldContextValue = {
  controlId: string;
  messageId: string;
  hasError: boolean;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

const useFormField = () => {
  const context = React.useContext(FormFieldContext);
  if (!context) {
    throw new Error("Form primitives must be used inside FormField.");
  }
  return context;
};

type FormFieldProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
  hasError?: boolean;
};

const FormField = ({ children, className, id, hasError = false }: FormFieldProps) => {
  const reactId = React.useId().replace(/:/g, "");
  const controlId = id || `field-${reactId}`;
  const messageId = `${controlId}-message`;

  return (
    <FormFieldContext.Provider value={{ controlId, messageId, hasError }}>
      <div className={cn("flex w-full flex-col gap-2", className)}>{children}</div>
    </FormFieldContext.Provider>
  );
};

type FormLabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;

const FormLabel = ({ className, ...props }: FormLabelProps) => {
  const { controlId, hasError } = useFormField();

  return (
    <label
      htmlFor={controlId}
      className={cn("text-sm font-medium text-[var(--gray-500)]", hasError && "text-red-600", className)}
      {...props}
    />
  );
};

export type FormControlElementProps = {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
};

type FormControlProps = {
  children: React.ReactElement<FormControlElementProps>;
};

const FormControl = ({ children }: FormControlProps) => {
  const { controlId, messageId, hasError } = useFormField();

  if (!React.isValidElement(children)) {
    return null;
  }

  const existingDescribedBy = children.props?.["aria-describedby"];

  const describedBy = [existingDescribedBy, messageId]
    .filter(Boolean)
    .join(" ");

  return React.cloneElement(children, {
    id: children.props.id || controlId,
    "aria-describedby": describedBy || undefined,
    "aria-invalid": hasError ? true : children.props["aria-invalid"],
  });
};

type FormMessageProps = React.HTMLAttributes<HTMLParagraphElement> & {
  error?: string;
  helperText?: string;
};

const FormMessage = ({ error, helperText, className, ...props }: FormMessageProps) => {
  const { messageId, hasError } = useFormField();
  const text = error || helperText;

  if (!text) return null;

  return (
    <p
      id={messageId}
      className={cn(hasError ? "text-sm text-red-500" : "text-xs text-gray-500", className)}
      {...props}
    >
      {text}
    </p>
  );
};

export { FormField, FormLabel, FormControl, FormMessage };
